import * as Dialog from "@radix-ui/react-dialog";
import { Settings as SettingsIcon, X } from "lucide-react";
import { useState } from "react";

import { AccountSection } from "./AccountSection";

/**
 * The gear menu: change your own password.
 *
 * MUCH smaller than the clinic system's, deliberately. There it opened a
 * modal with six sections -- account, organisation, workflow rules,
 * integrations, third-party APIs, push permissions -- because a settings
 * menu is where a clinical system accumulates everything nobody could
 * place elsewhere.
 *
 * Here the practice's identity and the mail account live on their own
 * Admin tabs, where an admin looks for them, and the two clinical
 * sections (workflow rules, PACS push) do not exist. What is left is the
 * one thing that belongs to the person rather than the installation, so
 * it is a single dialog rather than a menu of one.
 */
export function SettingsMenu({
  hideAdminConsole: _hideAdminConsole,
}: {
  /** Accepted and ignored -- the console is the only surface here. */
  hideAdminConsole?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          data-cy="btn-settings"
          aria-label="Account settings"
          title="Account settings"
          className="rounded-md p-1.5 text-foreground-on-chrome/80 hover:bg-nav-hover hover:text-foreground-on-chrome focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <SettingsIcon className="h-4 w-4" />
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          data-cy="settings-modal"
          className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-background-card shadow-theme-lg"
        >
          <div className="flex items-start justify-between gap-3 border-b border-border px-6 pt-5 pb-4">
            <div>
              <Dialog.Title className="text-lg font-semibold text-foreground-heading">
                Your account
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-xs text-foreground-muted">
                Change your password. Everything else about this
                installation lives under Admin.
              </Dialog.Description>
            </div>
            <Dialog.Close
              data-cy="btn-close-settings"
              aria-label="Close"
              className="rounded-md p-1 text-foreground-muted hover:bg-table-row-hover hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>
          <div className="px-6 py-5">
            <AccountSection />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
